import React, { useState, useEffect } from 'react';
import './MenuCatalog.css';

const MenuCatalog = ({ settings = {} }) => {
  const [language, setLanguage] = useState('de');

  // Function to get saved language
  const getSavedLanguage = () => {
    return localStorage.getItem('selectedLanguage') || 'de';
  };

  // Initialize language
  useEffect(() => {
    const savedLang = getSavedLanguage();
    setLanguage(savedLang);
  }, []);

  // Оновлюємо activeCategory при зміні мови
  useEffect(() => {
    setActiveCategory(language === 'en' ? 'All' : 'Alle');
  }, [language]);

  const {
    pageTitle = 'SPEISEKARTE',
    pageDescription = 'Entdecken Sie unsere authentische osteuropäische Küche',
    showSearch = true,
    searchPlaceholder = 'Produkte suchen...',
    showFilter = true,
    showSort = true,
    itemsPerPage = 12,
  } = settings;

  const categories =
    language === 'en'
      ? [
          'All',
          'SALAD / MEZZE',
          'Dumplings/Falafel',
          'Arabic Clubsandwich',
          'Vegetarian/Vegan Plates',
          'GRILL SPECIALTIES',
          'SHARING PLATES',
          'PIZZA',
          'Beverages',
        ]
      : [
          'Alle',
          'SALAT / MEZZE',
          'Teigtaschen/Falafel',
          'Arabisches Clubsandwich',
          'Vegetarische/Vegane Teller',
          'GRILLSPEZIALITÄTEN',
          'PLATTEN ZUM TEILEN',
          'PIZZA',
          'Getränke',
        ];

  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState(
    language === 'en' ? 'All' : 'Alle'
  );
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);

  // Load products from Shopify
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetch('/products.json?limit=100');
        const data = await response.json();

        const formattedProducts = data.products.map(product => ({
          id: product.id,
          title: product.title,
          description: product.body_html
            ?.replace(/<[^>]*>/g, '')
            .substring(0, 100),
          price: product.variants[0]?.price || '0.00',
          compareAtPrice: product.variants[0]?.compare_at_price,
          image: product.images[0]?.src || null,
          tags: product.tags || [],
          category: product.product_type?.toLowerCase() || '',
          variantId: product.variants[0]?.id || null,
          available: product.variants[0]?.available || false,
        }));

        setProducts(formattedProducts);
        setFilteredProducts(formattedProducts); // Показуємо всі продукти по дефолту
      } catch (error) {
        console.error('Error loading products:', error);
        // Fallback to mock data if API fails
        const mockProducts = [
          {
            id: 1,
            title: 'Grilled Salmon',
            description: 'Fresh Atlantic salmon with herbs',
            price: '28.50',
            image: 'https://via.placeholder.com/300x300',
            tags: ['seafood', 'fish'],
            category: 'seafood',
            variantId: 1,
            available: true,
          },
          // Add more mock products as needed
        ];
        setProducts(mockProducts);
        setFilteredProducts(mockProducts);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  useEffect(() => {
    const allCategory = language === 'en' ? 'All' : 'Alle';
    if (activeCategory === allCategory) {
      // Показуємо всі товари з усіх колекцій
      setFilteredProducts(products);
    } else {
      // Фільтруємо товари по категоріях
      const categoryLower = activeCategory.toLowerCase();
      const filtered = products.filter(product => {
        // Перевіряємо теги, категорію продукту та тип продукту
        return (
          product.tags?.some(tag =>
            tag.toLowerCase().includes(categoryLower)
          ) ||
          product.category?.toLowerCase().includes(categoryLower) ||
          product.product_type?.toLowerCase().includes(categoryLower) ||
          // Додаткові перевірки для специфічних категорій
          (categoryLower.includes('salat') &&
            (product.tags?.includes('salad') ||
              product.category === 'salad')) ||
          (categoryLower.includes('mezze') &&
            (product.tags?.includes('appetizer') ||
              product.category === 'appetizer')) ||
          (categoryLower.includes('falafel') &&
            (product.tags?.includes('falafel') ||
              product.title?.toLowerCase().includes('falafel'))) ||
          (categoryLower.includes('teigtaschen') &&
            (product.tags?.includes('dumpling') ||
              product.title?.toLowerCase().includes('dumpling'))) ||
          (categoryLower.includes('sandwich') &&
            (product.tags?.includes('sandwich') ||
              product.category === 'sandwich')) ||
          (categoryLower.includes('vegetarisch') &&
            (product.tags?.includes('vegetarian') ||
              product.tags?.includes('vegan'))) ||
          (categoryLower.includes('grill') &&
            (product.tags?.includes('grill') ||
              product.tags?.includes('bbq'))) ||
          (categoryLower.includes('platte') &&
            (product.tags?.includes('sharing') ||
              product.tags?.includes('platter'))) ||
          (categoryLower.includes('pizza') &&
            (product.tags?.includes('pizza') ||
              product.category === 'pizza')) ||
          (categoryLower.includes('getränke') &&
            (product.tags?.includes('beverage') ||
              product.category === 'beverage'))
        );
      });
      setFilteredProducts(filtered);
    }
  }, [activeCategory, products, language]);

  const handleCategoryClick = category => {
    setActiveCategory(category);
  };

  const addToCart = productId => {
    // Добавление в корзину через Shopify API
    fetch('/cart/add.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: productId,
        quantity: 1,
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }
        return response.json();
      })
      .then(data => {
        console.log('Product added to cart:', data);

        // Просто отримуємо оновлену кількість товарів без відправки подій
        // Header компонент буде оновлюватися через свій власний механізм
        fetch('/cart.js')
          .then(response => response.json())
          .then(cartData => {
            // Тихо відправляємо кастомну подію тільки для Header
            const headerUpdateEvent = new CustomEvent('header:cart-updated', {
              bubbles: true,
              detail: { itemCount: cartData.item_count },
            });
            document.dispatchEvent(headerUpdateEvent);
          });
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
      });
  };

  return (
    <div className='menu-catalog'>
      <div className='menu-catalog__container'>
        {/* Header */}
        <div className='menu-catalog__header'>
          <h1 className='menu-catalog__title'>{pageTitle}</h1>
          {pageDescription && (
            <p className='menu-catalog__description'>{pageDescription}</p>
          )}
        </div>

        {/* Main Content */}
        <div className='menu-catalog__content'>
          {/* Sidebar */}
          <div className='menu-catalog__sidebar'>
            <h3 className='menu-catalog__sidebar-title'>KATEGORIEN</h3>
            <div className='menu-catalog__sidebar-categories'>
              {categories.map(category => (
                <div
                  key={category}
                  className={`menu-catalog__sidebar-item ${
                    activeCategory === category ? 'active' : ''
                  }`}
                  onClick={() => handleCategoryClick(category)}
                >
                  {category === 'Alle' ? 'Alle Gerichte' : category}
                </div>
              ))}
            </div>
          </div>

          {/* Products */}
          <div className='menu-catalog__products'>
            {/* Toolbar */}
            <div className='menu-catalog__toolbar'>
              <div className='menu-catalog__toolbar-left'>
                <div className='menu-catalog__view-toggle'>
                  <button
                    className={`menu-catalog__view-btn ${
                      viewMode === 'grid' ? 'active' : ''
                    }`}
                    onClick={() => setViewMode('grid')}
                  >
                    ⊞
                  </button>
                  <button
                    className={`menu-catalog__view-btn ${
                      viewMode === 'list' ? 'active' : ''
                    }`}
                    onClick={() => setViewMode('list')}
                  >
                    ☰
                  </button>
                </div>
              </div>

              <div className='menu-catalog__toolbar-center'>
                <span className='menu-catalog__results-count'>
                  {filteredProducts.length} Artikel
                </span>
              </div>
            </div>

            {/* Products Grid */}
            <div
              className={`menu-catalog__grid ${viewMode === 'list' ? 'list-view' : ''}`}
            >
              {loading ? (
                <div className='menu-catalog__loading'>
                  Produkte werden geladen...
                </div>
              ) : filteredProducts.length > 0 ? (
                filteredProducts.map(product => (
                  <div key={product.id} className='menu-catalog__product-card'>
                    <div className='menu-catalog__product-image'>
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.title}
                          loading='lazy'
                        />
                      ) : (
                        <div className='menu-catalog__product-placeholder'>
                          {product.title?.slice(0, 2)}
                        </div>
                      )}
                    </div>

                    <div className='menu-catalog__product-info'>
                      <h3 className='menu-catalog__product-title'>
                        <a href={product.url}>{product.title}</a>
                      </h3>

                      {product.description && (
                        <p className='menu-catalog__product-description'>
                          {product.description.length > 80
                            ? product.description.slice(0, 80) + '...'
                            : product.description}
                        </p>
                      )}

                      <div className='menu-catalog__product-price'>
                        {product.compareAtPrice &&
                          product.compareAtPrice > product.price && (
                            <span className='menu-catalog__price-original'>
                              ${product.compareAtPrice}
                            </span>
                          )}
                        <span className='menu-catalog__price-current'>
                          ${product.price}
                        </span>
                      </div>

                      <button
                        className='menu-catalog__add-to-cart'
                        onClick={() => addToCart(product.variantId)}
                      >
                        In den Warenkorb
                      </button>
                    </div>

                    {product.tags && product.tags.length > 0 && (
                      <div className='menu-catalog__product-tags'>
                        {product.tags.slice(0, 3).map((tag, index) => (
                          <span key={index} className='menu-catalog__tag'>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className='menu-catalog__no-products'>
                  <p>
                    Keine Produkte gefunden. Versuchen Sie eine andere Kategorie
                    zu wählen.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuCatalog;
